import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Settings, Store, Users, Eye, EyeOff, Search, Plus, Pencil, X, Check, Building2, Wrench, MessageSquare, Bell, Plug, Boxes, Zap, Palette, Upload, Trash2, FileSpreadsheet, Send, ShieldCheck } from 'lucide-react';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.js';
import { useBrandContext } from '../shared/components/BrandContext.js';
import { Card } from '../shared/components/Card.js';
import { Button } from '../shared/components/Button.js';
import { Modal } from '../shared/components/Modal.js';
import { useToast } from '../shared/components/Toast.js';
import { apiFetch } from '../shared/hooks/useApi.js';
import { useStoreSettings, useUpdateStoreSettings, useTestConnection, type StoreSettings } from '../../modules/settings/hooks/useSettings.js';
import { DEFAULT_LABELS, AVAILABLE_ICONS, type IconName, type ModuleLabelsConfig } from '../shared/hooks/useModuleLabels.js';
import { ModuleIcon } from '../shared/utils/iconMap.js';
import type { Organization, UserListItem, UserRole } from '../shared/types/index.js';
import { AutomatiseringTab } from '../automation/AutomatiseringTab.js';
import { RechtenTab } from './RechtenTab.js';

type TabId = 'profiel' | 'winkels' | 'medewerkers' | 'rechten' | 'bedrijf' | 'huisstijl' | 'modules' | 'buitendienst' | 'communicatie' | 'notificaties' | 'integraties' | 'automatisering' | 'import';

const ROLES: { value: UserRole; label: string }[] = [
  { value: 'superadmin', label: 'Superadmin' },
  { value: 'hq', label: 'HQ' },
  { value: 'owner', label: 'Eigenaar' },
  { value: 'admin', label: 'Admin' },
  { value: 'manager', label: 'Winkeldirecteur (manager)' },
  { value: 'binnendienst', label: 'Verkoper / binnendienst' },
  { value: 'buitendienst', label: 'Buitendienst' },
  { value: 'chauffeur', label: 'Chauffeur' },
];

export function SettingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('tab') as TabId) || 'profiel';
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  const tabs: { id: TabId; label: string; icon: React.ReactNode; adminOnly: boolean }[] = [
    { id: 'profiel', label: 'Profiel', icon: <Settings size={16} />, adminOnly: false },
    { id: 'bedrijf', label: 'Bedrijf', icon: <Building2 size={16} />, adminOnly: true },
    { id: 'huisstijl', label: 'Huisstijl', icon: <Palette size={16} />, adminOnly: true },
    { id: 'modules', label: 'Modules', icon: <Boxes size={16} />, adminOnly: true },
    { id: 'buitendienst', label: 'Buitendienst', icon: <Wrench size={16} />, adminOnly: true },
    { id: 'communicatie', label: 'Communicatie', icon: <MessageSquare size={16} />, adminOnly: true },
    { id: 'notificaties', label: 'Notificaties', icon: <Bell size={16} />, adminOnly: true },
    { id: 'automatisering', label: 'Automatisering', icon: <Zap size={16} />, adminOnly: true },
    { id: 'integraties', label: 'Integraties', icon: <Plug size={16} />, adminOnly: true },
    { id: 'winkels', label: 'Winkels', icon: <Store size={16} />, adminOnly: true },
    { id: 'medewerkers', label: 'Medewerkers', icon: <Users size={16} />, adminOnly: true },
    { id: 'rechten', label: 'Rechten', icon: <ShieldCheck size={16} />, adminOnly: true },
    { id: 'import', label: 'Import', icon: <FileSpreadsheet size={16} />, adminOnly: true },
  ];

  const visibleTabs = tabs.filter(t => !t.adminOnly || isAdmin);

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Instellingen</h1>

      {/* Tab navigation */}
      <div className="flex gap-1 border-b border-gray-200">
        {visibleTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setSearchParams({ tab: tab.id })}
            className={clsx(
              'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px',
              activeTab === tab.id
                ? 'border-gold text-gold'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            )}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'profiel' && <ProfielTab />}
      {activeTab === 'bedrijf' && isAdmin && <BedrijfTab />}
      {activeTab === 'huisstijl' && isAdmin && <HuisstijlTab />}
      {activeTab === 'modules' && isAdmin && <ModulesTab />}
      {activeTab === 'buitendienst' && isAdmin && <BuitendienstTab />}
      {activeTab === 'communicatie' && isAdmin && <CommunicatieTab />}
      {activeTab === 'notificaties' && isAdmin && <NotificatiesTab />}
      {activeTab === 'automatisering' && isAdmin && <AutomatiseringTab />}
      {activeTab === 'integraties' && isAdmin && <IntegratiesTab />}
      {activeTab === 'winkels' && isAdmin && <WinkelsTab />}
      {activeTab === 'medewerkers' && isAdmin && <MedewerkersTab />}
      {activeTab === 'rechten' && isAdmin && <RechtenTab />}
      {activeTab === 'import' && isAdmin && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Klantdata importeren</h2>
              <p className="text-sm text-gray-500 mt-0.5">Importeer bestaande klantdossiers of apparaten via Excel of CSV</p>
            </div>
            <button
              onClick={() => navigate('/settings/import')}
              className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-gold rounded-lg hover:bg-gold/90 transition-colors"
            >
              <FileSpreadsheet size={16} />
              Importeren starten
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
              <h3 className="text-sm font-semibold text-blue-900 mb-1">Klanten & Dossiers</h3>
              <p className="text-xs text-blue-700">Importeer klantgegevens: naam, adres, contactinfo, bestelbonnummer en status. Elk rij wordt een nieuw dossier.</p>
              <ul className="mt-2 space-y-1 text-xs text-blue-600">
                <li>• Klantnaam (verplicht)</li>
                <li>• Adres, postcode, gemeente</li>
                <li>• Telefoon, e-mail</li>
                <li>• Bestelbonnummer, verkoper, status</li>
              </ul>
            </div>
            <div className="p-4 bg-amber-50 rounded-xl border border-amber-100">
              <h3 className="text-sm font-semibold text-amber-900 mb-1">Apparaten</h3>
              <p className="text-xs text-amber-700">Importeer apparaten gekoppeld aan bestaande dossiers via het bestelbonnummer.</p>
              <ul className="mt-2 space-y-1 text-xs text-amber-600">
                <li>• Bestelbonnummer (verplicht — koppelt aan dossier)</li>
                <li>• Naam apparaat (verplicht)</li>
                <li>• Merk, leverancier, serienummer</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================
// PROFIEL TAB
// =============================================

function ProfielTab() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [language, setLanguage] = useState(user?.language || 'nl');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  const profileMutation = useMutation({
    mutationFn: (data: { firstName: string; lastName: string; language: string }) =>
      apiFetch('/api/settings/profile', { method: 'PATCH', body: JSON.stringify(data) }),
    onSuccess: () => toast('success', 'Profiel bijgewerkt'),
    onError: () => toast('error', 'Fout bij bijwerken profiel'),
  });

  const passwordMutation = useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string }) =>
      apiFetch('/api/settings/password', { method: 'PATCH', body: JSON.stringify(data) }),
    onSuccess: () => {
      toast('success', 'Wachtwoord gewijzigd');
      setCurrentPassword('');
      setNewPassword('');
    },
    onError: (err: Error) => toast('error', err.message || 'Fout bij wijzigen wachtwoord'),
  });

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {/* Profiel */}
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Profiel</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input type="email" value={user?.email || ''} disabled
              className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Voornaam</label>
              <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Achternaam</label>
              <input type="text" value={lastName} onChange={e => setLastName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Taal</label>
            <select value={language} onChange={e => setLanguage(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none">
              <option value="nl">Nederlands</option>
              <option value="fr">Frans</option>
              <option value="en">Engels</option>
            </select>
          </div>
          <Button onClick={() => profileMutation.mutate({ firstName, lastName, language })}
            disabled={profileMutation.isPending}>
            {profileMutation.isPending ? 'Opslaan...' : 'Profiel opslaan'}
          </Button>
        </div>
      </Card>

      {/* Wachtwoord */}
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Wachtwoord wijzigen</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Huidig wachtwoord</label>
            <div className="relative">
              <input type={showCurrentPw ? 'text' : 'password'} value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none" />
              <button type="button" onClick={() => setShowCurrentPw(!showCurrentPw)}
                className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600">
                {showCurrentPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nieuw wachtwoord</label>
            <div className="relative">
              <input type={showNewPw ? 'text' : 'password'} value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none" />
              <button type="button" onClick={() => setShowNewPw(!showNewPw)}
                className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600">
                {showNewPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <Button onClick={() => passwordMutation.mutate({ currentPassword, newPassword })}
            disabled={passwordMutation.isPending || !currentPassword || !newPassword}>
            {passwordMutation.isPending ? 'Wijzigen...' : 'Wachtwoord wijzigen'}
          </Button>
        </div>
      </Card>
    </div>
  );
}

// =============================================
// BEDRIJF TAB
// =============================================

function BedrijfTab() {
  const { toast } = useToast();
  const { data: settings, isLoading } = useStoreSettings();
  const updateSettings = useUpdateStoreSettings();
  const [form, setForm] = useState({ naam: '', adres: '', telefoon: '', email: '', btwNummer: '' });

  useEffect(() => {
    if (settings?.bedrijf) setForm(settings.bedrijf);
  }, [settings]);

  const set = (key: string, value: string) => setForm(f => ({ ...f, [key]: value }));

  const handleSave = async () => {
    try {
      await updateSettings.mutateAsync({ bedrijf: form });
      toast('success', 'Bedrijfsinstellingen opgeslagen');
    } catch { toast('error', 'Fout bij opslaan'); }
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <Card>
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Bedrijfsgegevens</h2>
      <div className="space-y-4 max-w-lg">
        <FieldInput label="Bedrijfsnaam" value={form.naam} onChange={v => set('naam', v)} />
        <FieldInput label="Adres" value={form.adres} onChange={v => set('adres', v)} />
        <div className="grid grid-cols-2 gap-3">
          <FieldInput label="Telefoon" value={form.telefoon} onChange={v => set('telefoon', v)} />
          <FieldInput label="Email" value={form.email} onChange={v => set('email', v)} type="email" />
        </div>
        <FieldInput label="BTW-nummer" value={form.btwNummer} onChange={v => set('btwNummer', v)} placeholder="BE0123456789" />
        <Button onClick={handleSave} disabled={updateSettings.isPending}>
          {updateSettings.isPending ? 'Opslaan...' : 'Opslaan'}
        </Button>
      </div>
    </Card>
  );
}

// =============================================
// HUISSTIJL TAB — logo, kleuren, afzender (whitelabel)
// =============================================

interface BrandingForm {
  bedrijfsNaam: string; kleurPrimair: string; kleurSecundair: string; logoUrl: string | null;
  telefoon: string; email: string; website: string; adresRegel1: string; adresRegel2: string; btwNummer: string; bankrekening: string;
  emailReplyTo: string; emailFooterTekst: string;
}
const BRANDING_LEEG: BrandingForm = {
  bedrijfsNaam: '', kleurPrimair: '#C8A961', kleurSecundair: '#2C2C2C', logoUrl: null,
  telefoon: '', email: '', website: '', adresRegel1: '', adresRegel2: '', btwNummer: '', bankrekening: '', emailReplyTo: '', emailFooterTekst: '',
};
const HEX_RE = /^#[0-9a-fA-F]{6}$/;

function HuisstijlTab() {
  const { toast } = useToast();
  const { setEntityBrand } = useBrandContext();
  const { activeEntity } = useAuth();
  const [form, setForm] = useState<BrandingForm>(BRANDING_LEEG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const laad = async () => {
    try {
      const b = await apiFetch<Partial<BrandingForm>>('/api/settings/branding');
      setForm({ ...BRANDING_LEEG, ...Object.fromEntries(Object.entries(b).filter(([, v]) => v !== null && v !== undefined)), logoUrl: b.logoUrl ?? null });
    } catch { toast('error', 'Huisstijl laden mislukt'); } finally { setLoading(false); }
  };
  useEffect(() => { laad(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  const set = (k: keyof BrandingForm, v: string) => setForm(f => ({ ...f, [k]: v }));

  const pasToe = (b: Partial<BrandingForm>) => setEntityBrand({
    bedrijfsNaam: b.bedrijfsNaam || activeEntity?.name || null,
    logoUrl: b.logoUrl ?? null,
    kleurPrimair: b.kleurPrimair ?? null,
    kleurSecundair: b.kleurSecundair ?? null,
    entityId: activeEntity?.id ?? null,
  });

  const handleSave = async () => {
    if (!HEX_RE.test(form.kleurPrimair) || !HEX_RE.test(form.kleurSecundair)) { toast('error', 'Kleuren moeten een hex-code zijn, bv. #232323'); return; }
    setSaving(true);
    try {
      const { logoUrl: _l, ...body } = form;
      const b = await apiFetch<BrandingForm>('/api/settings/branding', { method: 'PATCH', body: JSON.stringify(body) });
      setForm(f => ({ ...f, ...b }));
      pasToe(b);
      toast('success', 'Huisstijl opgeslagen — mails en PDF\'s gebruiken vanaf nu deze instellingen');
    } catch (e) { toast('error', e instanceof Error ? e.message : 'Opslaan mislukt'); } finally { setSaving(false); }
  };

  const handleLogo = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const token = localStorage.getItem('accessToken');
      const res = await fetch('/api/settings/branding/logo', { method: 'POST', body: fd, headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'Upload mislukt');
      const { logoUrl } = await res.json() as { logoUrl: string };
      setForm(f => ({ ...f, logoUrl }));
      pasToe({ ...form, logoUrl });
      toast('success', 'Logo geüpload');
    } catch (e) { toast('error', e instanceof Error ? e.message : 'Upload mislukt'); } finally { setUploading(false); }
  };

  const handleLogoWeg = async () => {
    try {
      await apiFetch('/api/settings/branding/logo', { method: 'DELETE' });
      setForm(f => ({ ...f, logoUrl: null }));
      pasToe({ ...form, logoUrl: null });
    } catch { toast('error', 'Logo verwijderen mislukt'); }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Logo</h2>
        <p className="text-sm text-gray-500 mb-4">Wordt getoond op de loginpagina, in e-mails en op PDF's (offertes, werkbonnen, rapporten). PNG met transparante achtergrond werkt het best.</p>
        <div className="flex items-center gap-6">
          <div className="flex h-24 w-56 items-center justify-center rounded-xl border border-card-border bg-white p-3">
            {form.logoUrl ? <img src={form.logoUrl} alt="Logo" className="max-h-full max-w-full object-contain" /> : <span className="text-sm text-gray-400">Nog geen logo</span>}
          </div>
          <div className="flex flex-col gap-2">
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-dark">
              <Upload size={16} /> {uploading ? 'Uploaden…' : 'Logo kiezen'}
              <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" disabled={uploading} onChange={e => handleLogo(e.target.files?.[0])} />
            </label>
            {form.logoUrl && (
              <button type="button" onClick={handleLogoWeg} className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-red-600"><Trash2 size={14} /> Logo verwijderen</button>
            )}
          </div>
        </div>
      </Card>

      <Card>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Kleuren</h2>
        <p className="text-sm text-gray-500 mb-4">Primair = knoppen, links en accenten. Secundair = zijbalk en koptekst van e-mails en PDF's.</p>
        <div className="grid max-w-lg grid-cols-2 gap-4">
          {(['kleurPrimair', 'kleurSecundair'] as const).map(k => (
            <div key={k}>
              <label className="mb-1 block text-sm font-medium text-gray-700">{k === 'kleurPrimair' ? 'Primaire kleur' : 'Secundaire kleur'}</label>
              <div className="flex items-center gap-2">
                <input type="color" value={HEX_RE.test(form[k]) ? form[k] : '#000000'} onChange={e => set(k, e.target.value.toUpperCase())} className="h-10 w-12 cursor-pointer rounded border border-card-border p-0.5" />
                <input value={form[k]} onChange={e => set(k, e.target.value)} placeholder="#232323" className="w-full rounded-lg border border-card-border px-3 py-2 text-sm font-mono focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-center gap-3 rounded-xl p-4 text-white" style={{ background: HEX_RE.test(form.kleurSecundair) ? form.kleurSecundair : '#2C2C2C' }}>
          <span className="font-semibold">{form.bedrijfsNaam || activeEntity?.name || 'Voorbeeld'}</span>
          <span className="rounded-lg px-3 py-1 text-sm font-medium" style={{ background: HEX_RE.test(form.kleurPrimair) ? form.kleurPrimair : '#C8A961' }}>Knop</span>
          <span className="text-xs opacity-70">voorbeeld van koptekst + knop</span>
        </div>
      </Card>

      <Card>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Afzender en contact</h2>
        <p className="text-sm text-gray-500 mb-4">Deze gegevens staan in de kop en voet van e-mails en PDF's. Klanten die op een mail antwoorden, komen bij het reply-to-adres terecht.</p>
        <div className="max-w-lg space-y-4">
          <FieldInput label="Naam (afzender en koptekst)" value={form.bedrijfsNaam} onChange={v => set('bedrijfsNaam', v)} placeholder={activeEntity?.name} />
          <div className="grid grid-cols-2 gap-3">
            <FieldInput label="Telefoon" value={form.telefoon} onChange={v => set('telefoon', v)} />
            <FieldInput label="E-mail winkel" value={form.email} onChange={v => set('email', v)} type="email" />
          </div>
          <FieldInput label="Reply-to voor klantmails (leeg = e-mail winkel)" value={form.emailReplyTo} onChange={v => set('emailReplyTo', v)} type="email" />
          <FieldInput label="Website" value={form.website} onChange={v => set('website', v)} placeholder="www.voorbeeld.be" />
          <FieldInput label="Adres" value={form.adresRegel1} onChange={v => set('adresRegel1', v)} />
          <FieldInput label="Postcode en gemeente" value={form.adresRegel2} onChange={v => set('adresRegel2', v)} />
          <div className="grid grid-cols-2 gap-3">
            <FieldInput label="BTW-nummer (op offertes)" value={form.btwNummer} onChange={v => set('btwNummer', v)} placeholder="BE0123456789" />
            <FieldInput label="Rekeningnummer (op offertes)" value={form.bankrekening} onChange={v => set('bankrekening', v)} placeholder="BE.. .... .... ...." />
          </div>
          <FieldInput label="Slotregel in e-mails (optioneel)" value={form.emailFooterTekst} onChange={v => set('emailFooterTekst', v)} placeholder="bv. Ma-za 9u-18u · Meesters Raamstylisten sinds 1974" />
          <Button onClick={handleSave} disabled={saving}>{saving ? 'Opslaan…' : 'Huisstijl opslaan'}</Button>
        </div>
      </Card>
    </div>
  );
}

// =============================================
// MODULES TAB — configureerbare namen & iconen
// =============================================

const MODULE_KEYS: (keyof ModuleLabelsConfig)[] = ['buitendienst', 'logistiek', 'planning', 'communicatie', 'crm', 'winkel'];

function ModulesTab() {
  const { toast } = useToast();
  const { data: settings, isLoading } = useStoreSettings();
  const updateSettings = useUpdateStoreSettings();
  const [form, setForm] = useState<Record<string, { label: string; icon: IconName }>>({});
  const [editingKey, setEditingKey] = useState<string | null>(null);

  useEffect(() => {
    const custom = (settings as any)?.moduleLabels as Partial<ModuleLabelsConfig> | undefined;
    const initial: Record<string, { label: string; icon: IconName }> = {};
    for (const key of MODULE_KEYS) {
      initial[key] = {
        label: custom?.[key]?.label || DEFAULT_LABELS[key].label,
        icon: custom?.[key]?.icon || DEFAULT_LABELS[key].icon,
      };
    }
    setForm(initial);
  }, [settings]);

  const handleSave = async () => {
    try {
      const moduleLabels: Partial<ModuleLabelsConfig> = {};
      for (const key of MODULE_KEYS) {
        const val = form[key];
        if (val) {
          (moduleLabels as any)[key] = { label: val.label, icon: val.icon };
        }
      }
      await updateSettings.mutateAsync({ moduleLabels } as any);
      toast('success', 'Module instellingen opgeslagen');
      setEditingKey(null);
    } catch { toast('error', 'Fout bij opslaan'); }
  };

  const handleReset = (key: string) => {
    setForm(prev => ({
      ...prev,
      [key]: { ...DEFAULT_LABELS[key as keyof ModuleLabelsConfig] },
    }));
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Module namen & iconen</h2>
        <p className="text-sm text-gray-500 mb-6">
          Pas de weergavenamen en iconen van modules aan. Deze verschijnen in de navigatie en titelbalk.
        </p>

        <div className="space-y-3">
          {MODULE_KEYS.map(key => {
            const val = form[key];
            if (!val) return null;
            const isEditing = editingKey === key;
            const isDefault = val.label === DEFAULT_LABELS[key].label && val.icon === DEFAULT_LABELS[key].icon;

            return (
              <div key={key} className="border border-gray-200 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  {/* Current icon preview */}
                  <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-gray-600">
                    <ModuleIcon name={val.icon} size={20} />
                  </div>

                  {/* Module info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900">{val.label}</span>
                      {!isDefault && (
                        <span className="text-[10px] bg-gold/10 text-gold px-1.5 py-0.5 rounded-full font-medium">Aangepast</span>
                      )}
                    </div>
                    <span className="text-xs text-gray-400">Standaard: {DEFAULT_LABELS[key].label}</span>
                  </div>

                  {/* Edit button */}
                  <button
                    onClick={() => setEditingKey(isEditing ? null : key)}
                    className={clsx(
                      'p-2 rounded-lg transition-colors',
                      isEditing ? 'bg-gold/10 text-gold' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                    )}
                  >
                    <Pencil size={14} />
                  </button>
                </div>

                {/* Editing form */}
                {isEditing && (
                  <div className="mt-4 pt-4 border-t border-gray-100 space-y-4">
                    {/* Label input */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">Naam in navigatie</label>
                      <input
                        type="text"
                        value={val.label}
                        onChange={e => setForm(prev => ({ ...prev, [key]: { ...prev[key], label: e.target.value } }))}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none"
                        placeholder={DEFAULT_LABELS[key].label}
                      />
                    </div>

                    {/* Icon picker */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">Icoon</label>
                      <div className="flex flex-wrap gap-1.5">
                        {AVAILABLE_ICONS.map(iconName => (
                          <button
                            key={iconName}
                            onClick={() => setForm(prev => ({ ...prev, [key]: { ...prev[key], icon: iconName } }))}
                            className={clsx(
                              'w-9 h-9 rounded-lg flex items-center justify-center transition-all',
                              val.icon === iconName
                                ? 'bg-gold text-white ring-2 ring-gold/30'
                                : 'bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                            )}
                            title={iconName}
                          >
                            <ModuleIcon name={iconName} size={16} />
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Reset to default */}
                    {!isDefault && (
                      <button
                        onClick={() => handleReset(key)}
                        className="text-xs text-gray-400 hover:text-gray-600 underline"
                      >
                        Terugzetten naar standaard
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-6">
          <Button onClick={handleSave} disabled={updateSettings.isPending}>
            {updateSettings.isPending ? 'Opslaan...' : 'Module instellingen opslaan'}
          </Button>
        </div>
      </Card>
    </div>
  );
}

// =============================================
// BUITENDIENST TAB
// =============================================

function BuitendienstTab() {
  const { toast } = useToast();
  const { data: settings, isLoading } = useStoreSettings();
  const updateSettings = useUpdateStoreSettings();
  const [templates, setTemplates] = useState<string[]>([]);
  const [checklist, setChecklist] = useState<string[]>([]);
  const [autoNotificaties, setAutoNotificaties] = useState(true);
  const [newTemplate, setNewTemplate] = useState('');
  const [newCheckItem, setNewCheckItem] = useState('');

  useEffect(() => {
    if (settings?.buitendienst) {
      setTemplates(settings.buitendienst.trajectTemplates);
      setChecklist(settings.buitendienst.defaultChecklistItems);
      setAutoNotificaties(settings.buitendienst.autoNotificaties);
    }
  }, [settings]);

  const handleSave = async () => {
    try {
      await updateSettings.mutateAsync({
        buitendienst: { trajectTemplates: templates, defaultChecklistItems: checklist, autoNotificaties },
      });
      toast('success', 'Buitendienst instellingen opgeslagen');
    } catch { toast('error', 'Fout bij opslaan'); }
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Traject templates</h2>
        <div className="space-y-2 mb-3">
          {templates.map((t, i) => (
            <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
              <span className="text-sm text-gray-900 flex-1">{t}</span>
              <button onClick={() => setTemplates(ts => ts.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-500">
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <input type="text" value={newTemplate} onChange={e => setNewTemplate(e.target.value)}
            placeholder="Nieuwe stap..." onKeyDown={e => { if (e.key === 'Enter' && newTemplate.trim()) { setTemplates(ts => [...ts, newTemplate.trim()]); setNewTemplate(''); } }}
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none" />
          <Button variant="secondary" size="sm" onClick={() => { if (newTemplate.trim()) { setTemplates(ts => [...ts, newTemplate.trim()]); setNewTemplate(''); } }}>
            <Plus size={14} />
          </Button>
        </div>
      </Card>

      <Card>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Default checklist items</h2>
        <div className="space-y-2 mb-3">
          {checklist.map((c, i) => (
            <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
              <span className="text-sm text-gray-900 flex-1">{c}</span>
              <button onClick={() => setChecklist(cs => cs.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-500">
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <input type="text" value={newCheckItem} onChange={e => setNewCheckItem(e.target.value)}
            placeholder="Nieuw item..." onKeyDown={e => { if (e.key === 'Enter' && newCheckItem.trim()) { setChecklist(cs => [...cs, newCheckItem.trim()]); setNewCheckItem(''); } }}
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none" />
          <Button variant="secondary" size="sm" onClick={() => { if (newCheckItem.trim()) { setChecklist(cs => [...cs, newCheckItem.trim()]); setNewCheckItem(''); } }}>
            <Plus size={14} />
          </Button>
        </div>

        <div className="mt-6 pt-4 border-t border-gray-100">
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={autoNotificaties} onChange={e => setAutoNotificaties(e.target.checked)} className="w-4 h-4 accent-gold" />
            <span className="text-sm text-gray-700">Automatische notificaties bij statuswijzigingen</span>
          </label>
        </div>
      </Card>

      <div className="lg:col-span-2">
        <Button onClick={handleSave} disabled={updateSettings.isPending}>
          {updateSettings.isPending ? 'Opslaan...' : 'Opslaan'}
        </Button>
      </div>
    </div>
  );
}

// =============================================
// COMMUNICATIE TAB
// =============================================

function CommunicatieTab() {
  const { toast } = useToast();
  const { data: settings, isLoading } = useStoreSettings();
  const updateSettings = useUpdateStoreSettings();
  const [form, setForm] = useState({ emailSignatuur: '', smsTemplate: '', whatsappTemplate: '' });

  useEffect(() => {
    if (settings?.communicatie) setForm(settings.communicatie);
  }, [settings]);

  const set = (key: string, value: string) => setForm(f => ({ ...f, [key]: value }));

  const handleSave = async () => {
    try {
      await updateSettings.mutateAsync({ communicatie: form });
      toast('success', 'Communicatie instellingen opgeslagen');
    } catch { toast('error', 'Fout bij opslaan'); }
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-6 max-w-2xl">
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Email signatuur</h2>
        <textarea value={form.emailSignatuur} onChange={e => set('emailSignatuur', e.target.value)}
          rows={5} placeholder="Uw email signatuur..."
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none resize-none" />
      </Card>

      <Card>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">SMS Template</h2>
        <p className="text-xs text-gray-400 mb-2">Variabelen: {'{klantNaam}'}, {'{datum}'}, {'{dossierNummer}'}</p>
        <textarea value={form.smsTemplate} onChange={e => set('smsTemplate', e.target.value)}
          rows={3} placeholder="SMS template..."
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none resize-none" />
      </Card>

      <Card>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">WhatsApp Template</h2>
        <p className="text-xs text-gray-400 mb-2">Variabelen: {'{klantNaam}'}, {'{datum}'}, {'{dossierNummer}'}</p>
        <textarea value={form.whatsappTemplate} onChange={e => set('whatsappTemplate', e.target.value)}
          rows={3} placeholder="WhatsApp template..."
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none resize-none" />
      </Card>

      <Button onClick={handleSave} disabled={updateSettings.isPending}>
        {updateSettings.isPending ? 'Opslaan...' : 'Opslaan'}
      </Button>
    </div>
  );
}

// =============================================
// NOTIFICATIES TAB
// =============================================

const DATABUS_EVENTS = [
  { value: 'GOEDEREN_ONTVANGEN', label: 'Goederen ontvangen' },
  { value: 'GOEDEREN_GESCAND', label: 'Goederen gescand' },
  { value: 'LEVERING_GEPLAND', label: 'Levering gepland' },
  { value: 'LEVERING_VOLTOOID', label: 'Levering voltooid' },
  { value: 'VORDERINGSSTAAT_INGEDIEND', label: 'Vorderingsstaat ingediend' },
];

function NotificatiesTab() {
  const { toast } = useToast();
  const { data: settings, isLoading } = useStoreSettings();
  const updateSettings = useUpdateStoreSettings();
  const [emailNotificaties, setEmailNotificaties] = useState(true);
  const [pushNotificaties, setPushNotificaties] = useState(true);
  const [databusEvents, setDatabusEvents] = useState<string[]>([]);

  useEffect(() => {
    if (settings?.notificaties) {
      setEmailNotificaties(settings.notificaties.emailNotificaties);
      setPushNotificaties(settings.notificaties.pushNotificaties);
      setDatabusEvents(settings.notificaties.databusEvents);
    }
  }, [settings]);

  const toggleEvent = (event: string) => {
    setDatabusEvents(current =>
      current.includes(event) ? current.filter(e => e !== event) : [...current, event],
    );
  };

  const handleSave = async () => {
    try {
      await updateSettings.mutateAsync({ notificaties: { emailNotificaties, pushNotificaties, databusEvents } });
      toast('success', 'Notificatie instellingen opgeslagen');
    } catch { toast('error', 'Fout bij opslaan'); }
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-6 max-w-lg">
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Kanalen</h2>
        <div className="space-y-3">
          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-sm text-gray-700">Email notificaties</span>
            <input type="checkbox" checked={emailNotificaties} onChange={e => setEmailNotificaties(e.target.checked)} className="w-4 h-4 accent-gold" />
          </label>
          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-sm text-gray-700">Push notificaties</span>
            <input type="checkbox" checked={pushNotificaties} onChange={e => setPushNotificaties(e.target.checked)} className="w-4 h-4 accent-gold" />
          </label>
        </div>
      </Card>

      <Card>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Databus events</h2>
        <p className="text-xs text-gray-400 mb-3">Selecteer welke WMS events een notificatie genereren</p>
        <div className="space-y-2">
          {DATABUS_EVENTS.map(evt => (
            <label key={evt.value} className="flex items-center gap-3 cursor-pointer py-1">
              <input type="checkbox" checked={databusEvents.includes(evt.value)}
                onChange={() => toggleEvent(evt.value)} className="w-4 h-4 accent-gold" />
              <span className="text-sm text-gray-700">{evt.label}</span>
            </label>
          ))}
        </div>
      </Card>

      <Button onClick={handleSave} disabled={updateSettings.isPending}>
        {updateSettings.isPending ? 'Opslaan...' : 'Opslaan'}
      </Button>
    </div>
  );
}

// =============================================
// INTEGRATIES TAB
// =============================================

function IntegratiesTab() {
  const { toast } = useToast();
  const { data: settings, isLoading } = useStoreSettings();
  const updateSettings = useUpdateStoreSettings();
  const testConnection = useTestConnection();
  const [form, setForm] = useState({ wmsWebhookUrl: '', databusApiKey: '', makecomWebhookUrl: '' });
  const [showApiKey, setShowApiKey] = useState(false);

  useEffect(() => {
    if (settings?.integraties) setForm(settings.integraties);
  }, [settings]);

  const set = (key: string, value: string) => setForm(f => ({ ...f, [key]: value }));

  const handleSave = async () => {
    try {
      await updateSettings.mutateAsync({ integraties: form });
      toast('success', 'Integratie instellingen opgeslagen');
    } catch { toast('error', 'Fout bij opslaan'); }
  };

  const handleTest = async () => {
    if (!form.wmsWebhookUrl) { toast('error', 'Vul eerst een WMS webhook URL in'); return; }
    try {
      const result = await testConnection.mutateAsync(form.wmsWebhookUrl);
      toast(result.success ? 'success' : 'error', result.message);
    } catch { toast('error', 'Test mislukt'); }
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-6 max-w-lg">
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">WMS Koppeling</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Webhook URL</label>
            <div className="flex gap-2">
              <input type="url" value={form.wmsWebhookUrl} onChange={e => set('wmsWebhookUrl', e.target.value)}
                placeholder="https://wms.example.com/webhook"
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none" />
              <Button variant="secondary" size="sm" onClick={handleTest} disabled={testConnection.isPending}>
                {testConnection.isPending ? 'Testen...' : 'Test'}
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <Card>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Databus</h2>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
          <div className="relative">
            <input type={showApiKey ? 'text' : 'password'} value={form.databusApiKey}
              onChange={e => set('databusApiKey', e.target.value)} placeholder="sk-..."
              className="w-full px-3 py-2 pr-10 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none" />
            <button type="button" onClick={() => setShowApiKey(!showApiKey)}
              className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600">
              {showApiKey ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>
      </Card>

      <Button onClick={handleSave} disabled={updateSettings.isPending}>
        {updateSettings.isPending ? 'Opslaan...' : 'Opslaan'}
      </Button>

      <DemoResetSection />
    </div>
  );
}

// =============================================
// DEMO RESET
// =============================================

function DemoResetSection() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showConfirm, setShowConfirm] = useState(false);

  const { data: demoStatus } = useQuery({
    queryKey: ['demo-status'],
    queryFn: () => apiFetch<{
      configured: boolean;
      entityName: string;
      counts: { dossiers: number; leads: number; importLogs: number };
      totalRecords: number;
    }>('/api/admin/demo/status'),
  });

  const resetMutation = useMutation({
    mutationFn: () => apiFetch('/api/admin/demo/reset', { method: 'POST' }),
    onSuccess: (data: any) => {
      toast('success', `Demo gereset: ${data.deleted?.dossiers || 0} dossiers verwijderd`);
      setShowConfirm(false);
      queryClient.invalidateQueries({ queryKey: ['demo-status'] });
    },
    onError: () => toast('error', 'Fout bij resetten demo account'),
  });

  if (!demoStatus?.configured) return null;

  return (
    <>
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Demo Account</h2>
        <p className="text-sm text-gray-500 mb-4">
          Wis alle data van de demo-entity ({demoStatus.entityName}) na een sales demo.
        </p>
        <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3 mb-4">
          <div className="text-sm text-gray-600">
            <span className="font-medium">{demoStatus.counts?.dossiers || 0}</span> dossiers,{' '}
            <span className="font-medium">{demoStatus.counts?.leads || 0}</span> leads,{' '}
            <span className="font-medium">{demoStatus.counts?.importLogs || 0}</span> imports
          </div>
          <span className="text-xs text-gray-400">{demoStatus.totalRecords || 0} records totaal</span>
        </div>
        <Button variant="secondary" onClick={() => setShowConfirm(true)}
          disabled={demoStatus.totalRecords === 0}
          className="!bg-red-50 !text-red-600 hover:!bg-red-100 !border-red-200">
          Reset Demo
        </Button>
      </Card>

      {showConfirm && (
        <Modal open={showConfirm} onClose={() => setShowConfirm(false)} title="Demo resetten?">
          <p className="text-sm text-gray-600 mb-4">
            Dit verwijdert <strong>alle {demoStatus.totalRecords} records</strong> van de demo-entity
            ({demoStatus.entityName}): dossiers, apparaten, leads, imports, berichten en documenten.
          </p>
          <p className="text-sm text-red-600 font-medium mb-6">
            Deze actie kan niet ongedaan worden.
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setShowConfirm(false)}>Annuleren</Button>
            <Button onClick={() => resetMutation.mutate()}
              disabled={resetMutation.isPending}
              className="!bg-red-600 hover:!bg-red-700 !text-white">
              {resetMutation.isPending ? 'Resetten...' : 'Ja, reset alles'}
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
}

// =============================================
// SHARED HELPERS
// =============================================

function LoadingSpinner() {
  return (
    <div className="flex justify-center py-12">
      <div className="w-6 h-6 border-2 border-gold border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function FieldInput({ label, value, onChange, type = 'text', placeholder }: {
  label: string; value: string; onChange: (v: string) => void; type?: string; placeholder?: string;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none" />
    </div>
  );
}

// =============================================
// WINKELS TAB
// =============================================

function WinkelsTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editOrg, setEditOrg] = useState<Organization | null>(null);

  const { data: organizations = [], isLoading } = useQuery<Organization[]>({
    queryKey: ['settings', 'organizations'],
    queryFn: () => apiFetch('/api/settings/organizations'),
  });

  const filtered = organizations.filter(org =>
    org.name.toLowerCase().includes(search.toLowerCase()) ||
    (org.city && org.city.toLowerCase().includes(search.toLowerCase()))
  );

  const toggleMutation = useMutation({
    mutationFn: (org: Organization) =>
      apiFetch(`/api/settings/organizations/${org.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ active: !org.active }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'organizations'] });
      toast('success', 'Status bijgewerkt');
    },
  });

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-2.5 text-gray-400" />
          <input type="text" placeholder="Zoek op naam of stad..." value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none" />
        </div>
        <Button onClick={() => { setEditOrg(null); setModalOpen(true); }}>
          <Plus size={16} /> Nieuwe winkel
        </Button>
      </div>

      {/* Tabel */}
      <Card padding={false}>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-gold border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-400">Geen winkels gevonden</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Naam</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Stad</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Telefoon</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Email</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">BTW-nr</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Medewerkers</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Status</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(org => (
                  <tr key={org.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{org.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{org.city || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{org.phone || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{org.email || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{org.vatNumber || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{org._count?.users ?? 0}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleMutation.mutate(org)}
                        className={clsx(
                          'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border',
                          org.active
                            ? 'bg-green-50 text-green-700 border-green-200'
                            : 'bg-gray-50 text-gray-500 border-gray-200'
                        )}
                      >
                        <span className={clsx('w-1.5 h-1.5 rounded-full', org.active ? 'bg-green-500' : 'bg-gray-400')} />
                        {org.active ? 'Actief' : 'Inactief'}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => { setEditOrg(org); setModalOpen(true); }}
                        className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600">
                        <Pencil size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Modal */}
      {modalOpen && (
        <OrganizationModal
          organization={editOrg}
          onClose={() => { setModalOpen(false); setEditOrg(null); }}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['settings', 'organizations'] });
            setModalOpen(false);
            setEditOrg(null);
          }}
        />
      )}
    </div>
  );
}

function OrganizationModal({ organization, onClose, onSaved }: {
  organization: Organization | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const isEdit = !!organization;
  const [form, setForm] = useState({
    name: organization?.name || '',
    type: organization?.type || 'franchise',
    vatNumber: organization?.vatNumber || '',
    address: organization?.address || '',
    city: organization?.city || '',
    zip: organization?.zip || '',
    country: organization?.country || 'BE',
    phone: organization?.phone || '',
    email: organization?.email || '',
    googleReviewUrl: organization?.googleReviewUrl || '',
  });

  const set = (key: string, value: string) => setForm(f => ({ ...f, [key]: value }));

  const mutation = useMutation({
    mutationFn: () => {
      if (isEdit) {
        return apiFetch(`/api/settings/organizations/${organization.id}`, {
          method: 'PATCH', body: JSON.stringify(form),
        });
      }
      return apiFetch('/api/settings/organizations', {
        method: 'POST', body: JSON.stringify(form),
      });
    },
    onSuccess: () => {
      toast('success', isEdit ? 'Winkel bijgewerkt' : 'Winkel aangemaakt');
      onSaved();
    },
    onError: (err: Error) => toast('error', err.message || 'Fout bij opslaan'),
  });

  return (
    <Modal open onClose={onClose} title={isEdit ? 'Winkel bewerken' : 'Nieuwe winkel'} size="lg">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Naam *</label>
            <input type="text" value={form.name} onChange={e => set('name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select value={form.type} onChange={e => set('type', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none">
              <option value="franchise">Franchise</option>
              <option value="holding">Holding</option>
              <option value="operational">Operationeel</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">BTW-nummer</label>
            <input type="text" value={form.vatNumber} onChange={e => set('vatNumber', e.target.value)}
              placeholder="BE0123456789"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none" />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Adres</label>
            <input type="text" value={form.address} onChange={e => set('address', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
            <input type="text" value={form.zip} onChange={e => set('zip', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Stad</label>
            <input type="text" value={form.city} onChange={e => set('city', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Land</label>
            <input type="text" value={form.country} onChange={e => set('country', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Telefoon</label>
            <input type="text" value={form.phone} onChange={e => set('phone', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none" />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input type="email" value={form.email} onChange={e => set('email', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none" />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Google Reviews link</label>
            <input type="url" value={form.googleReviewUrl} onChange={e => set('googleReviewUrl', e.target.value)}
              placeholder="https://g.page/r/..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none" />
            <p className="text-xs text-gray-400 mt-1">Klanten met score ≥ 3 worden doorgestuurd naar deze link. Leeg laten om Google Reviews te overslaan.</p>
          </div>
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose}>Annuleren</Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending || !form.name}>
            {mutation.isPending ? 'Opslaan...' : isEdit ? 'Bijwerken' : 'Aanmaken'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// =============================================
// MEDEWERKERS TAB
// =============================================

function MedewerkersTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [filterEntityId, setFilterEntityId] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editUser, setEditUser] = useState<UserListItem | null>(null);
  const uitnodigen = useMutation({
    mutationFn: (u: UserListItem) => apiFetch<{ verstuurd: boolean; soort: string; fout: string | null }>(`/api/settings/users/${u.id}/uitnodigen`, { method: 'POST' }),
    onSuccess: (r, u) => r.verstuurd
      ? toast('success', `Uitnodiging verstuurd naar ${u.email}${r.soort === 'extern' ? ' (externe plaatser)' : ''}`)
      : toast('error', r.fout ? `Niet verstuurd: ${r.fout}` : 'Niet verstuurd'),
    onError: () => toast('error', 'Uitnodiging versturen mislukt'),
  });

  const { data: users = [], isLoading } = useQuery<UserListItem[]>({
    queryKey: ['settings', 'users', filterEntityId, search],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filterEntityId) params.set('entityId', filterEntityId);
      if (search) params.set('search', search);
      return apiFetch(`/api/settings/users?${params.toString()}`);
    },
  });

  const { data: organizations = [] } = useQuery<Organization[]>({
    queryKey: ['settings', 'organizations'],
    queryFn: () => apiFetch('/api/settings/organizations'),
  });

  // Inactieve medewerkers standaard verborgen (lijst blijft overzichtelijk na opkuis van dubbels)
  const [toonInactief, setToonInactief] = useState(false);
  const aantalInactief = users.filter(u => !u.active).length;
  const zichtbaar = toonInactief ? users : users.filter(u => u.active);

  const toggleMutation = useMutation({
    mutationFn: (u: UserListItem) =>
      apiFetch(`/api/settings/users/${u.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ active: !u.active }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'users'] });
      toast('success', 'Status bijgewerkt');
    },
  });

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-1">
          <div className="relative flex-1 max-w-sm">
            <Search size={16} className="absolute left-3 top-2.5 text-gray-400" />
            <input type="text" placeholder="Zoek op naam of email..." value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none" />
          </div>
          <select value={filterEntityId} onChange={e => setFilterEntityId(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none">
            <option value="">Alle winkels</option>
            {organizations.filter(o => o.active).map(org => (
              <option key={org.id} value={org.id}>{org.name}</option>
            ))}
          </select>
          {aantalInactief > 0 && (
            <label className="flex items-center gap-2 text-sm text-gray-600 whitespace-nowrap cursor-pointer">
              <input type="checkbox" checked={toonInactief} onChange={e => setToonInactief(e.target.checked)}
                className="rounded border-gray-300 text-gold focus:ring-gold/30" />
              Toon inactieve ({aantalInactief})
            </label>
          )}
        </div>
        <Button onClick={() => { setEditUser(null); setModalOpen(true); }}>
          <Plus size={16} /> Nieuwe medewerker
        </Button>
      </div>

      {/* Tabel */}
      <Card padding={false}>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-gold border-t-transparent rounded-full animate-spin" />
          </div>
        ) : zichtbaar.length === 0 ? (
          <div className="text-center py-12 text-gray-400">Geen medewerkers gevonden</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Naam</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Email</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Rol</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Winkel</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Status</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {zichtbaar.map(u => (
                  <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {u.firstName} {u.lastName}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{u.email}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-700 capitalize">
                        {u.role}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {u.entityAccess && u.entityAccess.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {u.entityAccess.map((ea: any) => (
                            <span key={ea.entityId} className="inline-flex px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600">
                              {ea.entity?.name || '—'}
                            </span>
                          ))}
                        </div>
                      ) : u.entity?.name || '—'}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleMutation.mutate(u)}
                        className={clsx(
                          'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border',
                          u.active
                            ? 'bg-green-50 text-green-700 border-green-200'
                            : 'bg-gray-50 text-gray-500 border-gray-200'
                        )}
                      >
                        <span className={clsx('w-1.5 h-1.5 rounded-full', u.active ? 'bg-green-500' : 'bg-gray-400')} />
                        {u.active ? 'Actief' : 'Inactief'}
                      </button>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <button onClick={() => { setEditUser(u); setModalOpen(true); }}
                        className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600" title="Bewerken">
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => uitnodigen.mutate(u)} disabled={uitnodigen.isPending}
                        className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 disabled:opacity-40" title="Uitnodiging (opnieuw) versturen">
                        <Send size={14} />
                      </button>
                      {u.extern && <span className="ml-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">extern</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Modal */}
      {modalOpen && (
        <UserModal
          user={editUser}
          organizations={organizations}
          onClose={() => { setModalOpen(false); setEditUser(null); }}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['settings', 'users'] });
            setModalOpen(false);
            setEditUser(null);
          }}
        />
      )}
    </div>
  );
}

function UserModal({ user, organizations, onClose, onSaved }: {
  user: UserListItem | null;
  organizations: Organization[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const isEdit = !!user;
  const [form, setForm] = useState({
    email: user?.email || '',
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    role: user?.role || 'binnendienst',
    entityId: user?.entityId || '',
    language: user?.language || 'nl',
    password: '',
    extern: !!user?.extern,
  });
  const [selectedEntityIds, setSelectedEntityIds] = useState<string[]>(
    user?.entityAccess?.map((ea: any) => ea.entityId) || (user?.entityId ? [user.entityId] : [])
  );
  const [sendInvite, setSendInvite] = useState(!isEdit);

  const set = (key: string, value: string | boolean) => setForm(f => ({ ...f, [key]: value }));
  const isPlaatserRol = form.role === 'buitendienst' || form.role === 'chauffeur';

  const toggleEntity = (id: string) => {
    setSelectedEntityIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const mutation = useMutation({
    mutationFn: () => {
      if (isEdit) {
        const { password, email, ...data } = form;
        // E-mail (= login) enkel meesturen als ze gewijzigd is
        const nieuwEmail = email.trim().toLowerCase() !== (user.email ?? '').toLowerCase() ? email.trim() : undefined;
        return apiFetch(`/api/settings/users/${user.id}`, {
          method: 'PATCH', body: JSON.stringify({ ...data, ...(nieuwEmail ? { email: nieuwEmail } : {}), entityIds: selectedEntityIds }),
        });
      }
      return apiFetch('/api/settings/users', {
        method: 'POST', body: JSON.stringify({
          ...form,
          password: sendInvite ? undefined : form.password,
          entityIds: selectedEntityIds,
          sendInvite,
        }),
      });
    },
    onSuccess: () => {
      toast('success', isEdit ? 'Medewerker bijgewerkt' : (sendInvite ? 'Medewerker aangemaakt en uitnodiging verstuurd' : 'Medewerker aangemaakt'));
      onSaved();
    },
    onError: (err: Error) => toast('error', err.message || 'Fout bij opslaan'),
  });

  return (
    <Modal open onClose={onClose} title={isEdit ? 'Medewerker bewerken' : 'Nieuwe medewerker'} size="lg">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Voornaam *</label>
            <input type="text" value={form.firstName} onChange={e => set('firstName', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Achternaam *</label>
            <input type="text" value={form.lastName} onChange={e => set('lastName', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none" />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <input type="email" value={form.email} onChange={e => set('email', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none" />
            {isEdit && form.email.trim().toLowerCase() !== (user.email ?? '').toLowerCase() && (
              <p className="text-xs text-amber-700 mt-1">Dit is ook het login-adres: de medewerker logt voortaan in met het nieuwe e-mailadres.</p>
            )}
          </div>
          {!isEdit && !sendInvite && (
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Wachtwoord *</label>
              <input type="password" value={form.password} onChange={e => set('password', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none" />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rol</label>
            <select value={form.role} onChange={e => set('role', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none">
              {ROLES.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>
          {isPlaatserRol && (
            <div className="col-span-2">
              <label className="flex items-start gap-2 cursor-pointer p-3 rounded-lg border border-gray-200 bg-gray-50">
                <input type="checkbox" checked={form.extern} onChange={e => set('extern', e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-gray-300 text-gold focus:ring-gold/50" />
                <span>
                  <span className="block text-sm font-medium text-gray-700">Externe plaatser / onderaannemer</span>
                  <span className="block text-xs text-gray-500">Ziet enkel zijn eigen afspraken en de documenten daarbij: geen bedragen, geen andere dossiers, geen winkel-schermen.</span>
                </span>
              </label>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Taal</label>
            <select value={form.language} onChange={e => set('language', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none">
              <option value="nl">Nederlands</option>
              <option value="fr">Frans</option>
              <option value="en">Engels</option>
            </select>
          </div>
          {/* Multi-store toewijzing */}
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Winkels</label>
            <div className="grid grid-cols-2 gap-2">
              {organizations.filter(o => o.active && o.type === 'store').map(org => (
                <label key={org.id} className={clsx(
                  'flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors',
                  selectedEntityIds.includes(org.id)
                    ? 'bg-gold/10 border-gold/30 text-gray-900'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                )}>
                  <input type="checkbox" checked={selectedEntityIds.includes(org.id)}
                    onChange={() => toggleEntity(org.id)}
                    className="rounded border-gray-300 text-gold focus:ring-gold/30" />
                  <span className="text-sm">{org.name}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        {/* Invite optie */}
        {!isEdit && (
          <label className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-blue-50 border border-blue-100 cursor-pointer">
            <input type="checkbox" checked={sendInvite} onChange={e => setSendInvite(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500/30" />
            <span className="text-sm text-blue-800">Verstuur uitnodigingsemail (medewerker kiest zelf wachtwoord)</span>
          </label>
        )}
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose}>Annuleren</Button>
          <Button onClick={() => mutation.mutate()}
            disabled={mutation.isPending || !form.firstName || !form.lastName || !form.email || (!isEdit && !sendInvite && !form.password)}>
            {mutation.isPending ? 'Opslaan...' : isEdit ? 'Bijwerken' : (sendInvite ? 'Aanmaken & uitnodigen' : 'Aanmaken')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
